/**
 * What the phone can say about where the rider is.
 *
 * Signals only. Which phase the journey is in, and what the app does about it, belongs to
 * `rider-journey.ts` — this just reports what the fixes add up to.
 *
 * Pure, because every transition here only happens on a real morning: the drive starting, the
 * fix degrading in the urban canyon outside the station, the phone going underground. Kept as
 * a reducer over explicit signals, those mornings can be replayed from a desk.
 */

import type { Coordinates } from "../shared/types";
import { distanceMeters } from "../shared/time";

/**
 * Close enough to the boarding station that the train, not the drive, is the story.
 *
 * Halved from 250 m on 2026-09-01. At 250 m it latched Glen Park while Eben was 246 m out and
 * still six and a half minutes from the platform, and he pushed the phase back by hand; at 125 m
 * the same morning latches about forty seconds before he says so himself.
 */
export const AT_STATION_METERS = 125;

/**
 * Stations that need a tighter one, because something fast passes close by.
 *
 * Rockridge sits in the median of Highway 24, so a car doing thirty on the freeway is well
 * inside a fence sized for a street. On 2026-08-27 it latched 246 m out at 13.3 m/s, with Eben
 * still in the car; eighteen seconds later he was 37 m away, so nothing is lost by waiting — and
 * 37 m is what keeps this at 50 m rather than lower.
 */
const TIGHT_BOARDING_METERS: Record<string, number> = { ROCK: 50 };

export function boardingRadiusFor(stationId: string | null | undefined): number {
	return (stationId == null ? undefined : TIGHT_BOARDING_METERS[stationId]) ?? AT_STATION_METERS;
}

/**
 * Tighter than the boarding fence, because this one ends the journey rather than starting it.
 *
 * Getting there early is only a nuisance at the start; at the far end it takes the on-train card
 * away from someone still on the train. Halved from 120 m on 2026-09-01, where the first fix on
 * the Rockridge platform read 25 m: 60 m still latches on that same fix, and the 74 m one a
 * quarter of a minute later stops counting as being there.
 */
export const ARRIVAL_METERS = 60;

/**
 * How close counts as being at the place past the platform, where the profile does not say.
 *
 * A house or a car park is not a station. This shared `ARRIVAL_METERS` by accident and moved with
 * it, so tightening the platform fence quietly redrew the doorstep.
 */
export const FINAL_PLACE_METERS = 120;

/**
 * How far from his stop a fresh fix must be to say his due train is not in yet.
 *
 * Station exits sit well outside the 60 m platform fence — Civic Center's is 79–131 m from the
 * centroid — while the late train this guard exists for was still at the previous stop, 2.4 km
 * out. The closest adjacent BART stations are several times this far apart.
 */
export const CONTRADICTS_ARRIVAL_METERS = 400;

/** How far from his stop the street outside it reaches, and how sharp a fix must be to say so. */
export const STATION_EXIT_METERS = 150;
export const STATION_EXIT_ACCURACY_METERS = 30;

export interface JourneySignals {
	/**
	 * The journey is under way, so a station latch belongs to it rather than to yesterday.
	 *
	 * On the school run that is "away from the house". On the ride home there is nothing to have
	 * left — the app has no coordinates for school — so the journey is under way as soon as it is
	 * the one being looked at.
	 */
	underWay: boolean;
	/** On the train, which is what lets the timetable speak for the far end. */
	boarded: boolean;
	nowMs: number;
}

/** What the latches are measured from. Any of these may be missing. */
export interface StationFix {
	/** Accuracy rides along: at the far end it decides whether the fix counts at all. */
	position: (Coordinates & { accuracyMeters?: number | null }) | null;
	/** When the position was measured; an old surface fix must not overrule an underground arrival. */
	fixAtMs?: number | null;
	boardingStation: Coordinates | null;
	/** How close counts as being at it. Not every station can afford the same answer. */
	boardingRadiusMeters: number;
	/** The stop he gets off at, so reaching it can end the ride. */
	destinationStation: Coordinates | null;
	/** When his train is due at that stop, for the times there is no fix to be had. */
	destinationDueMs: number | null;
	/** Where this leg begins, when that is a place rather than a house. The ride home starts at school. */
	startPlace: Coordinates | null;
	startRadiusMeters: number;
	/** A bounded start place whose shape cannot be represented by a radius. */
	startBoundary?: readonly Coordinates[] | null;
	/** Where he is actually going, past the platform. The ride home has two of them. */
	finalPlaces: Coordinates[];
	/** How close counts as being there. A car park is a smaller thing than a station. */
	finalRadiusMeters: number;
	/** A bounded final place whose shape cannot be represented by a radius. */
	finalBoundary?: readonly Coordinates[] | null;
}

export interface JourneyState {
	/**
	 * Latched. The fix is at its worst exactly here — an urban canyon, then underground — so
	 * falling back out of the station on one bad reading would be worse than staying put.
	 */
	atStation: boolean;
	/** Off the train, at the far end. */
	atDestination: boolean;
	/** All the way there. */
	arrived: boolean;
	/** Seen at the place this leg starts from, which is the only thing that makes leaving it mean anything. */
	atStart: boolean;
	/** Was there, demonstrably is not now. On the ride home, that is the shuttle down the hill. */
	leftStart: boolean;
}

export const INITIAL_JOURNEY_STATE: JourneyState = {
	atStation: false,
	atDestination: false,
	arrived: false,
	atStart: false,
	leftStart: false,
};

function within(position: Coordinates | null, place: Coordinates | null, meters: number): boolean {
	return position != null && place != null && distanceMeters(position, place) <= meters;
}

export function insideBoundary(position: Coordinates | null, boundary: readonly Coordinates[]): boolean {
	if (position == null || boundary.length < 3) {
		return false;
	}
	let inside = false;
	for (let index = 0, previous = boundary.length - 1; index < boundary.length; previous = index++) {
		const a = boundary[index];
		const b = boundary[previous];
		if (a && b && a.lat > position.lat !== b.lat > position.lat) {
			const crossingLon = ((b.lon - a.lon) * (position.lat - a.lat)) / (b.lat - a.lat) + a.lon;
			if (position.lon < crossingLon) {
				inside = !inside;
			}
		}
	}
	return inside;
}

function metersToBoundary(position: Coordinates, boundary: readonly Coordinates[]): number {
	const latitudeScale = 111_320;
	const longitudeScale = latitudeScale * Math.cos((position.lat * Math.PI) / 180);
	let nearest = Number.POSITIVE_INFINITY;
	for (let index = 0; index < boundary.length; index += 1) {
		const a = boundary[index];
		const b = boundary[(index + 1) % boundary.length];
		if (!a || !b) {
			continue;
		}
		const ax = (a.lon - position.lon) * longitudeScale;
		const ay = (a.lat - position.lat) * latitudeScale;
		const bx = (b.lon - position.lon) * longitudeScale;
		const by = (b.lat - position.lat) * latitudeScale;
		const dx = bx - ax;
		const dy = by - ay;
		const lengthSquared = dx * dx + dy * dy;
		const fraction = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / lengthSquared));
		nearest = Math.min(nearest, Math.hypot(ax + fraction * dx, ay + fraction * dy));
	}
	return nearest;
}

/** The complete accuracy circle is outside, rather than only its reported centre. */
export function clearlyOutsideBoundary(position: StationFix["position"], boundary: readonly Coordinates[]): boolean {
	return position != null && !insideBoundary(position, boundary) && metersToBoundary(position, boundary) > (position.accuracyMeters ?? 0);
}

/** The complete accuracy circle is inside, rather than only its reported centre. */
export function clearlyInsideBoundary(position: StationFix["position"], boundary: readonly Coordinates[]): boolean {
	return position != null && insideBoundary(position, boundary) && metersToBoundary(position, boundary) > (position.accuracyMeters ?? 0);
}

/**
 * The far-end fences fire on the first fix, so accuracy does the work a dwell used to.
 *
 * Waiting out twenty seconds read as a fault to the rider, who was off the train and walking
 * while the app still had him on it. A fix vaguer than the fence it claims to be inside is the
 * real danger: underground the reported accuracy has run to kilometres, and one of those landing
 * near the platform would end the ride while the train was still moving.
 */
/** A fix vaguer than the fence it is being measured against cannot settle anything. */
function usableFix(position: StationFix["position"], meters: number): boolean {
	return position != null && (position.accuracyMeters == null || position.accuracyMeters <= meters);
}

function arrivedAt(fix: StationFix, places: Array<Coordinates | null>, meters: number): boolean {
	const position = fix.position;
	if (!usableFix(position, meters)) {
		return false;
	}
	return places.some((place) => within(position, place, meters));
}

/** A usable location fix inside the far-end station fence, distinct from a train ETA. */
export function observedAtDestination(fix: StationFix): boolean {
	return arrivedAt(fix, [fix.destinationStation], ARRIVAL_METERS);
}

/**
 * A sharp fix at the street outside his stop: he has walked out of it, whatever the train says.
 *
 * The platform fence is 60 m, but the exits are not on the platform — on 2026-10-07 Civic Center's
 * stairs and the kerb past them read 79–131 m out at 14 m accuracy. Only once he is aboard, so a
 * walk past the station before the ride cannot end it, and only on a fix tight enough that an
 * underground guess cannot pass for the street.
 */
export function observedLeavingDestination(fix: StationFix): boolean {
	const position = fix.position;
	return (
		position != null &&
		fix.destinationStation != null &&
		position.accuracyMeters != null &&
		position.accuracyMeters <= STATION_EXIT_ACCURACY_METERS &&
		within(position, fix.destinationStation, STATION_EXIT_METERS)
	);
}

/**
 * His train is due, so he is there — whatever the fixes say, or fail to.
 *
 * Half the stops he gets off at are underground, where the reported accuracy runs to kilometres
 * and the fence is right to throw every one of them away. That left the end of the ride home
 * waiting on him to press a button. The timetable can answer instead: it is a claim about the
 * train rather than about him, so it is only allowed to speak once he has said he is on it.
 *
 * Verified 2026-08-26: the predicted Civic Center arrival moved nineteen seconds across a
 * twenty-five minute ride, and he pressed four seconds before it.
 *
 * A fresh accurate fix may still overrule it, but only one that puts him a station away. On
 * 2026-10-07 the Civic Center exit and the kerb past it read 79–131 m out at 14 m accuracy, and
 * "outside the 60 m fence" vetoed the timetable for half an hour while he walked to the car.
 */
function trainIsIn(signals: JourneySignals, fix: StationFix): boolean {
	const freshContradiction =
		fix.destinationStation != null &&
		fix.position != null &&
		fix.fixAtMs != null &&
		signals.nowMs - fix.fixAtMs <= 30_000 &&
		usableFix(fix.position, ARRIVAL_METERS) &&
		distanceMeters(fix.position, fix.destinationStation) > CONTRADICTS_ARRIVAL_METERS;
	return signals.boarded && fix.destinationDueMs != null && signals.nowMs >= fix.destinationDueMs && !freshContradiction;
}

/** A sighting at the far-end fence, or his train's own timetable. */
export type DestinationEvidence = "fence" | "timetable";

/**
 * Which of the two answered.
 *
 * The state itself only records that he is off the train, so a kilometre-wide fix and a nine-metre
 * one arrive looking identical — and on 2026-09-15 a timetable claim was announced as a sighting.
 */
export function destinationEvidence(signals: JourneySignals, fix: StationFix): DestinationEvidence | null {
	if (observedAtDestination(fix) || (signals.boarded && observedLeavingDestination(fix))) {
		return "fence";
	}
	return trainIsIn(signals, fix) ? "timetable" : null;
}

export function nextJourneyState(state: JourneyState, signals: JourneySignals, fix: StationFix): JourneyState {
	// A journey that has not begun un-latches everything, so yesterday's arrival is not still in
	// force this morning.
	if (!signals.underWay) {
		return state.atStation || state.atDestination || state.arrived ? INITIAL_JOURNEY_STATE : state;
	}

	// Where he is standing is the whole answer. Both fences sit at the end of their own leg —
	// the far-end platform and the place past it — so neither is somewhere this journey passes
	// through on the way. Requiring a boarding first only meant the app disbelieved his own feet.
	const atDestination = state.atDestination || destinationEvidence(signals, fix) != null;
	const arrived =
		state.arrived ||
		(fix.finalBoundary ? clearlyInsideBoundary(fix.position, fix.finalBoundary) : arrivedAt(fix, fix.finalPlaces, fix.finalRadiusMeters));
	const atStation = state.atStation || within(fix.position, fix.boardingStation, fix.boardingRadiusMeters);
	/*
	 * Leaving the place the leg starts from.
	 *
	 * "Not at school" is true of the whole world, so it says nothing until the phone has seen him
	 * there — hence the sighting first. Leaving then needs a fix good enough to be sure of: a
	 * vague one that happens to land outside the fence is not evidence of a shuttle.
	 */
	const atStart =
		state.atStart ||
		(fix.startBoundary
			? clearlyInsideBoundary(fix.position, fix.startBoundary)
			: arrivedAt(fix, [fix.startPlace], fix.startRadiusMeters));
	const outsideStart = fix.startBoundary
		? clearlyOutsideBoundary(fix.position, fix.startBoundary)
		: fix.startPlace != null && usableFix(fix.position, fix.startRadiusMeters) && !within(fix.position, fix.startPlace, fix.startRadiusMeters);
	const leftStart =
		state.leftStart ||
		(atStart && outsideStart);

	if (
		atStation === state.atStation &&
		atDestination === state.atDestination &&
		arrived === state.arrived &&
		atStart === state.atStart &&
		leftStart === state.leftStart
	) {
		return state;
	}
	return { atStation, atDestination, arrived, atStart, leftStart };
}

/**
 * The train the alerts keep describing once the drive has begun.
 *
 * A train that becomes unreachable is worth seeing slip away; silently swapping it for a later
 * one hides the only fact that matters. Swiping replaces the pin, which is how you give up on
 * a tight train.
 *
 * Before the drive a pin is kept but not *invented*: choosing a train in the kitchen is a real
 * choice and the card should honour it, while the recommendation stays what the alerts are about
 * until he is actually on his way.
 *
 * A new morning starts on its target train rather than whichever train happens to be next in the
 * deck. Aboard is the one case where the recommendation must not fill an empty pin. The recommendation
 * answers "which train should I catch from here", and the answer to that is always a train he is
 * not on — so a lost pin has to stay lost until something says which train he is actually riding.
 */
export function nextPinnedTrainId(current: string | null, recommendedId: string | null, boarded = false): string | null {
	if (current) {
		return current;
	}
	return boarded ? null : recommendedId;
}
